import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { PaymentService } from '../../api/generated/services/PaymentService';
import { ApiError } from '../../api/generated/core/ApiError';
import { isCouponReason } from '../../utils/couponReasons';
import { helper } from '../../utils/helper';
import type { PaymentCreateRequest } from '../../api/generated/models/PaymentCreateRequest';
import type { PaymentView } from '../../api/generated/models/PaymentView';
import type { PaymentCheckView } from '../../api/generated/models/PaymentCheckView';
import { setError } from './errorSlice';
import { addNotification } from './notificationSlice';
import { startLoading, stopLoading } from './loadingSlice';

// Changed from Invoice to Payment but keeping slice name for compatibility
export type PaymentUI = {
  id: string;
  paymentLink?: string;
  message?: string;
  status: 'Paid' | 'Not paid' | 'Cancelled';
};

interface InvoiceState {
  currentPayment: PaymentUI | null;
  items: PaymentUI[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  isLoaded: boolean;
}

const initialState: InvoiceState = {
  currentPayment: null,
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  loading: false,
  error: null,
  isLoaded: false,
};

/**
 * Returns the coupon `errorCode` of a rejected (400) payment request, or null.
 * Coupon rejections are handled at the coupon field by the caller, so the
 * generic error notification is skipped for them; a 400 without a known
 * coupon code is not a coupon error.
 */
export const getCouponApiError = (err: unknown): string | null => {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const body = helper.parseApiErrorBody(err);
  return isCouponReason(body?.errorCode) ? body.errorCode : null;
};

export const createPayment = createAsyncThunk(
  'invoice/createPayment',
  async (request: PaymentCreateRequest, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());
    try {
      const response: PaymentView = await PaymentService.postAppPaymentCreate(request);
      return {
        id: response.paymentId,
        paymentLink: response.paymentLink,
        message: response.message,
        status: 'Not paid' as const
      } satisfies PaymentUI;
    } catch (err) {
      // Coupon errors are surfaced at the coupon field by the caller - skip
      // the global notification and hand the exact error string over via
      // `rejectWithValue` (a plain `throw` would be serialized by RTK and
      // the `ApiError` body would be lost for `unwrap()` callers).
      const couponError = getCouponApiError(err);
      if (couponError) {
        return rejectWithValue({ couponError });
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to create payment';
      dispatch(setError(errorMessage));
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      }));
      throw err;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const checkPayment = createAsyncThunk(
  'invoice/checkPayment',
  async (paymentId: string, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response: PaymentCheckView = await PaymentService.getAppPaymentCheck(paymentId);
      return {
        id: response.paymentId,
        status: response.isPaid ? 'Paid' as const : 'Not paid' as const
      } satisfies PaymentUI;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check payment';
      dispatch(setError(errorMessage));
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      }));
      throw err;
    } finally {
      dispatch(stopLoading());
    }
  }
);

// Alias for backward compatibility
export const createInvoice = createPayment;
export const checkInvoicePayment = checkPayment;

const invoiceSlice = createSlice({
  name: 'invoice',
  initialState,
  reducers: {
    clearCurrentPayment: (state) => {
      state.currentPayment = null;
    },
    // Alias for backward compatibility
    clearCurrentInvoice: (state) => {
      state.currentPayment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPayment = action.payload;
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create payment';
      })
      .addCase(checkPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkPayment.fulfilled, (state, action) => {
        state.loading = false;
        // Update status of current payment if it matches
        if (state.currentPayment && state.currentPayment.id === action.payload.id) {
          state.currentPayment = action.payload;
        }
      })
      .addCase(checkPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to check payment';
      });
  },
});

export const { clearCurrentPayment, clearCurrentInvoice } = invoiceSlice.actions;
export default invoiceSlice.reducer;
